import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  Zap, Award, AlertTriangle, ShieldCheck, MapPin, RefreshCw, Users,
} from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";
import { useAuth } from "@/lib/auth";
import { computeMatch, buildCloseRateMap } from "./dispatchAssistLogic";

// Re-export so any existing consumers of DispatchAssist.jsx still work.
export { computeMatch, buildCloseRateMap };

// ============================================================================
// Dispatch Assist — CSR tool for evaluating incoming jobs against Tech Profiles
// and ServiceTitan job stats (Item 4, Phase 1, 2026-07-31).
//
// Matching logic is a direct port of smart_dispatch_assist_prototype.html —
// preserved rules:
//   1. Territory filter — in-territory first; cross-territory fallback if none.
//   2. Avoid-tag filter — excludes techs whose avoid list includes the selected
//      customer tag.
//   3. Permit / Very-High license gate — restricts to master/journeyman.
//   4. Tier-based ranking:
//        Low     → prefer `developing` (coaching reps)
//        Med/High/VeryHigh → prefer `top`/`mid`
//   5. Tag-based sort — techs matching the selected customer tag rank first.
//   6. Top 2 candidates shown, each with tier badge:
//        top → "Top closer", mid → "Strong fit", other → "Coaching rep"
//
// Job type dropdown uses ONLY the 3 real ServiceTitan categories (data-
// granularity decision — no 10-category illustrative array from the prototype).
// Value estimates are seeded from a small in-code map; when
// /api/dispatch-assist/job-stats has averaged data for the selected job_type,
// we prefer that live number over the seed.
// ============================================================================

// The 3 real ServiceTitan categories (locked; drift is a 422 on the backend).
const JOB_TYPES = [
  {
    id: "A-RESIDENTIAL SERVICE WORK",
    label: "A-Residential service work",
    baseTier: "Low",
    est: [180, 400],
  },
  {
    id: "COMMERCIAL SERVICE WORK",
    label: "Commercial service work",
    baseTier: "High",
    est: [2500, 8000],
  },
  {
    id: "Emergency",
    label: "Emergency",
    baseTier: "Medium",
    est: [400, 1200],
  },
];

// Flags — same set + bump semantics as the prototype. `bump: 1` bumps the tier
// up one level (Low→Medium, Medium→High, High→VeryHigh, capped at VeryHigh).
const FLAGS = [
  { id: "no_power", label: "No power / safety hazard described", bump: 1 },
  { id: "price_pushback", label: "Customer pushed hard on price up front", bump: 0 },
  { id: "permit", label: "Sounds like it needs a permit", bump: 1 },
  { id: "urgent_today", label: "Wants someone out today", bump: 0 },
];

// Customer relationship tags — must match token values used in tech_profile
// `relationship_tags` and `avoid_tags`. Kept identical to the prototype token
// set so any existing tag data flows through unchanged.
const CUSTOMER_TAGS = [
  { id: "farm", label: "Farm / rural property" },
  { id: "hobbyist", label: "Hobbyist / mechanically inclined" },
  { id: "punctual", label: "Very particular about arrival time" },
  { id: "detail", label: "Wants close attention to detail / finish quality" },
  { id: "commercial_pm", label: "Commercial / property manager / warranty co." },
  { id: "generator", label: "Generator-related" },
  { id: "none", label: "None of these fit" },
];

const TIER_ORDER = ["Low", "Medium", "High", "VeryHigh"];
const TIER_LABEL = { Low: "Low", Medium: "Medium", High: "High", VeryHigh: "Very high" };

const TIER_TONE = {
  Low:       { bg: "#e6f9f2", fg: "#0e8a68", border: "#c1eddc" },
  Medium:    { bg: "#e7f4fb", fg: "#1c6a99", border: "#c5e0ee" },
  High:      { bg: "#fff4d6", fg: "#8a5b00", border: "#f3d97a" },
  VeryHigh:  { bg: "#fff0f0", fg: "#7a1c1c", border: "#f5c2c2" },
};

// Addition 1 — priority messages, ported verbatim from scope doc.
const PRIORITY_MESSAGES = {
  Low:      "Standard priority. Book into the suggested tech's next open slot. No need to hold this over other calls in queue right now.",
  Medium:   "Standard priority. Book into the suggested tech's next open slot. No need to hold this over other calls in queue right now.",
  High:     "Elevated priority. If choosing between this and a Low/Medium tier call for the same open slot, this one wins. Never bump a customer who already has a committed time — only applies to calls still being decided.",
  VeryHigh: "Top priority. Book this over any Low/Medium/High tier call still being decided for the same slot. Book now — don't wait on Missie or Brett, they're being alerted in parallel in case the day's schedule needs adjusting.",
};

const TECH_BADGE_LABEL = (tier) =>
  tier === "top" ? "Top closer" : tier === "mid" ? "Strong fit" : "Coaching rep";

const LICENSE_LABEL = {
  master: "Master",
  journeyman: "Journeyman",
  non_journeyman: "Non-Journeyman",
  apprentice: "Apprentice",
};

function fmtMoney(n) {
  return "$" + Math.round(n).toLocaleString();
}


// ============================================================================
// Matching logic + close-rate helpers live in ./dispatchAssistLogic.js so the
// jest suite can import them without pulling react-router.
// ============================================================================


// ============================================================================
// Root
// ============================================================================
export default function DispatchAssist() {
  const { slug } = useParams();
  const { user } = useAuth();

  const [techs, setTechs] = useState([]);
  const [techsLoad, setTechsLoad] = useState({ status: "loading", error: null });
  const [statsIndex, setStatsIndex] = useState({});  // { [job_type]: {avg_ticket, close_rate, sample_size} }
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [closeRates, setCloseRates] = useState({});  // { [tech_name]: close_rate_pct 0–100 }
  const [closeRatesWeek, setCloseRatesWeek] = useState(null);

  // Form state
  const [jobTypeId, setJobTypeId] = useState("");
  const [location, setLocation] = useState("Springfield");
  const [flagsChecked, setFlagsChecked] = useState({});
  const [tag, setTag] = useState(null);

  // Result state
  const [result, setResult] = useState(null);   // { tier, valueEstimate, top2, usedCrossTerritory, permitGated, permit, alertFired }

  const loadTechs = async () => {
    setTechsLoad({ status: "loading", error: null });
    try {
      const { data } = await api.get("/tech-profiles", { params: { active: true, tenant_slug: slug } });
      setTechs(Array.isArray(data) ? data : []);
      setTechsLoad({ status: "ok", error: null });
    } catch (err) {
      setTechs([]);
      setTechsLoad({ status: "error", error: extractApiError(err) });
    }
  };
  const loadStats = async () => {
    try {
      const { data } = await api.get("/dispatch-assist/job-stats", { params: { tenant_slug: slug } });
      // Schema (2026-07-31 revision): one aggregate row per job_type — no per-tech
      // dimension. Index directly by job_type; the UI reads avg_ticket + sample_size
      // (+ optional median/min/max) to render the value range.
      const idx = {};
      (Array.isArray(data) ? data : []).forEach((r) => {
        if (r?.job_type) idx[r.job_type] = r;
      });
      setStatsIndex(idx);
    } catch { /* stats are opportunistic; empty is a valid state */ }
    setStatsLoaded(true);
  };
  // Reuse Claire / Big Board PDF snapshot — same source that powers the
  // "Close Rate — Technicians (Field)" tile on the L10 page. NO new endpoint,
  // NO new ingest. Percentages already normalized to 0-100 via
  // buildCloseRateMap's auto-scale rule.
  const loadCloseRates = async () => {
    try {
      const { data } = await api.get("/l10/scorecard/vps-snapshot", {
        params: { week: "latest_reporting", tenant_slug: slug },
      });
      const rows = data?.snapshot?.tech_performance || [];
      setCloseRates(buildCloseRateMap(rows));
      setCloseRatesWeek(data?.week_of || null);
    } catch { /* silent — ranking falls back to tag/tier ordering when the snapshot is unavailable */ }
  };

  useEffect(() => { loadTechs(); loadStats(); loadCloseRates(); }, [slug]);

  const evaluate = async () => {
    if (!jobTypeId) {
      window.alert("Pick a job type first.");
      return;
    }
    const jt = JOB_TYPES.find((j) => j.id === jobTypeId);
    const flagObjs = FLAGS.filter((f) => flagsChecked[f.id]);
    const bump = flagObjs.reduce((n, f) => n + f.bump, 0);
    const permit = !!flagsChecked.permit;
    const tagVal = tag && tag !== "none" ? tag : null;

    let tierIdx = TIER_ORDER.indexOf(jt.baseTier);
    tierIdx = Math.min(tierIdx + (bump > 0 ? 1 : 0), TIER_ORDER.length - 1);
    const finalTier = TIER_ORDER[tierIdx];

    // Value estimate — prefer live aggregate from job-stats when present,
    // fall back to the seed range otherwise. When VPS provides min/max we
    // use them directly; otherwise fall back to a ±25% band around avg_ticket.
    // The bump multiplier still applies so a permit-flagged job reflects the
    // escalation surcharge.
    const estMultiplier = bump > 0 ? 1.15 : 1;
    let low, high;
    const live = statsIndex[jt.id];
    if (live && (live.sample_size || 0) > 5) {
      if (typeof live.min_ticket === "number" && typeof live.max_ticket === "number") {
        low = live.min_ticket * estMultiplier;
        high = live.max_ticket * estMultiplier;
      } else {
        low = live.avg_ticket * 0.75 * estMultiplier;
        high = live.avg_ticket * 1.25 * estMultiplier;
      }
    } else {
      low = jt.est[0] * estMultiplier;
      high = jt.est[1] * estMultiplier;
    }

    const { ranked, usedCrossTerritory, permitGated } = computeMatch({
      techs, location, tag: tagVal, permit, finalTier, closeRates,
    });
    const top2 = ranked.slice(0, 2);

    let alertFired = false;
    if (finalTier === "VeryHigh" || permit) {
      // Best-effort mock-Slack alert. Failure is silent — the CSR should never
      // be blocked from continuing dispatch work if the alert log fails.
      try {
        await api.post("/dispatch-assist/alerts", {
          reason: (finalTier === "VeryHigh" && permit) ? "both"
                : finalTier === "VeryHigh" ? "very_high_tier"
                : "permit_flagged",
          job_type: jt.id,
          tier: TIER_LABEL[finalTier],
          location,
          value_estimate: [Math.round(low), Math.round(high)],
          top_candidates: top2.map((t) => ({ name: t.name, tier: t.tier, license: t.license })),
          customer_tag: tagVal,
        }, { params: { tenant_slug: slug } });
        alertFired = true;
      } catch { /* silent */ }
    }

    setResult({
      job: jt,
      finalTier,
      valueLow: low,
      valueHigh: high,
      bumpApplied: bump > 0,
      liveStats: live || null,
      top2,
      closeRates,
      closeRatesWeek,
      usedCrossTerritory,
      permitGated,
      permit,
      tagVal,
      alertFired,
    });
  };

  const toggleFlag = (id) => setFlagsChecked((f) => ({ ...f, [id]: !f[id] }));

  const territoryOptions = useMemo(() => {
    const set = new Set();
    techs.forEach((t) => (t.home_territory || []).forEach((h) => set.add(h)));
    // Ensure prototype defaults exist so the picker isn't empty when no profiles have been added yet.
    ["Springfield", "Joplin", "Central MO"].forEach((x) => set.add(x));
    return Array.from(set).sort();
  }, [techs]);

  const hasTechs = techs.length > 0;
  const canEvaluate = !!user && ["platform_admin", "owner", "administrative", "call_center"].includes(user.role);

  return (
    <div className="space-y-6" data-testid="dispatch-assist-page">
      <header>
        <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
          Dispatch Pilot · Phase 1
        </div>
        <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1 inline-flex items-center gap-2.5">
          <Zap size={22} strokeWidth={1.7} className="text-[#0072bc]" />
          Smart Dispatch Assist
        </h1>
        <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1 max-w-3xl">
          Type in what the customer told you. Get a value tier and a suggested tech in two seconds — no math, no guessing. You can always override.
        </p>
      </header>

      {techsLoad.status !== "ok" || !hasTechs ? (
        <PanelState
          status={techsLoad.status} error={techsLoad.error}
          isEmpty={!hasTechs}
          emptyTitle="No active tech profiles yet"
          emptyBody="Ask a platform admin to add technicians via Admin → Tech Profiles before running dispatch."
          testIdPrefix="dispatch-assist"
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <FormCard
            jobTypeId={jobTypeId} onJobTypeChange={setJobTypeId}
            location={location} onLocationChange={setLocation}
            territoryOptions={territoryOptions}
            flagsChecked={flagsChecked} onFlagToggle={toggleFlag}
            tag={tag} onTagChange={setTag}
            onEvaluate={evaluate} canEvaluate={canEvaluate}
          />
          {result ? (
            <ResultCard result={result} statsLoaded={statsLoaded} />
          ) : (
            <div className="border border-dashed border-[#e7e9ee] rounded-xl p-8 flex items-center justify-center text-[13px] text-[color:var(--cc-ink-muted)] bg-white/50 min-h-[320px]" data-testid="dispatch-assist-placeholder">
              Fill out the left card and click <b className="mx-1">Get recommendation</b> to see the result.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Left card — job input form
// ============================================================================
function FormCard({
  jobTypeId, onJobTypeChange,
  location, onLocationChange, territoryOptions,
  flagsChecked, onFlagToggle,
  tag, onTagChange,
  onEvaluate, canEvaluate,
}) {
  return (
    <div className="border border-[#e7e9ee] rounded-xl bg-white p-5 space-y-5 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-[#0072bc] text-white text-[13px] font-bold inline-flex items-center justify-center">1</span>
        <h2 className="font-display font-bold text-[17px] text-[#0e1420]">What&apos;s the job?</h2>
      </div>

      <Field label="Job type">
        <select
          data-testid="dispatch-job-type"
          value={jobTypeId} onChange={(e) => onJobTypeChange(e.target.value)}
          className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0072bc]"
        >
          <option value="">Select a job type…</option>
          {JOB_TYPES.map((j) => <option key={j.id} value={j.id}>{j.label}</option>)}
        </select>
      </Field>

      <Field label="Location">
        <select
          data-testid="dispatch-location"
          value={location} onChange={(e) => onLocationChange(e.target.value)}
          className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0072bc]"
        >
          {territoryOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>

      <Field label="Anything else the customer mentioned?">
        <div className="space-y-2">
          {FLAGS.map((f) => (
            <label key={f.id} className="flex items-start gap-2 text-[13px] cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid={`dispatch-flag-${f.id}`}
                checked={!!flagsChecked[f.id]}
                onChange={() => onFlagToggle(f.id)}
                className="accent-[#0072bc] mt-1"
              />
              <span>{f.label}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="If it fits, tag the customer (optional — helps match the right tech)">
        <div className="space-y-2">
          {CUSTOMER_TAGS.map((t) => (
            <label key={t.id} className="flex items-start gap-2 text-[13px] cursor-pointer select-none">
              <input
                type="radio"
                name="dispatch-customer-tag"
                data-testid={`dispatch-tag-${t.id}`}
                checked={tag === t.id}
                onChange={() => onTagChange(t.id)}
                className="accent-[#0072bc] mt-1"
              />
              <span>{t.label}</span>
            </label>
          ))}
        </div>
      </Field>

      <button
        onClick={onEvaluate}
        disabled={!canEvaluate}
        data-testid="dispatch-go-btn"
        className="w-full sm:w-auto inline-flex items-center gap-2 px-5 py-2.5 bg-[#0072bc] text-white text-[13px] font-semibold rounded-lg hover:bg-[#005a95] disabled:opacity-50"
      >
        <Zap size={15} strokeWidth={2} /> Get recommendation
      </button>
      {!canEvaluate && (
        <p className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
          Your role can view this page but not fire alerts. Sign in as CSR/admin to submit.
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">{label}</span>
      {children}
    </label>
  );
}


// ============================================================================
// Right card — evaluation result
// ============================================================================
function ResultCard({ result, statsLoaded }) {
  const tone = TIER_TONE[result.finalTier];
  const {
    job, finalTier, valueLow, valueHigh, bumpApplied, liveStats,
    top2, closeRates, closeRatesWeek,
    usedCrossTerritory, permitGated, permit, tagVal, alertFired,
  } = result;

  return (
    <div className="border border-[#e7e9ee] rounded-xl bg-white p-5 space-y-4 shadow-sm" data-testid="dispatch-result-card">
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-[#0072bc] text-white text-[13px] font-bold inline-flex items-center justify-center">2</span>
        <h2 className="font-display font-bold text-[17px] text-[#0e1420]">Here&apos;s what the data says</h2>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="dispatch-tier-badge"
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12.5px] font-semibold uppercase tracking-wider border"
          style={{ background: tone.bg, color: tone.fg, borderColor: tone.border }}
        >
          <Award size={13} strokeWidth={2} /> {TIER_LABEL[finalTier]} tier
        </span>
        {job.label && (
          <span className="text-[12.5px] text-[color:var(--cc-ink-muted)]">
            {job.label}
          </span>
        )}
      </div>

      <div>
        <div className="font-mono-data text-[22px] font-semibold text-[#0e1420]" data-testid="dispatch-value-estimate">
          {fmtMoney(valueLow)} – {fmtMoney(valueHigh)}
        </div>
        <p className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5">
          {liveStats
            ? `Based on this tenant's ${liveStats.sample_size} historical jobs at this category`
            : "Based on illustrative averages until Agent 12 job stats arrive"}
          {bumpApplied ? ", bumped up for urgency/permit signals." : "."}
        </p>
      </div>

      {/* Priority-message strip (Addition 1) */}
      <div
        data-testid="dispatch-priority-note"
        className="rounded-lg p-3 text-[12.5px] leading-relaxed border"
        style={{ background: tone.bg, color: tone.fg, borderColor: tone.border }}
      >
        <div className="uppercase tracking-widest text-[10px] font-semibold mb-1">Priority</div>
        {PRIORITY_MESSAGES[finalTier]}
      </div>

      {/* Top 2 candidates */}
      <div className="space-y-3" data-testid="dispatch-tech-list">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold flex items-center gap-1.5">
          <Users size={12} strokeWidth={2} /> Suggested techs
        </div>
        {top2.length === 0 ? (
          <div className="text-[13px] text-[color:var(--cc-ink-muted)] italic">
            No tech matches this filter. Consider relaxing the location, permit, or customer-tag constraints.
          </div>
        ) : (
          top2.map((t) => {
            const cr = closeRates?.[t.name];
            const hasCr = typeof cr === "number" && !Number.isNaN(cr);
            return (
            <div key={t.id || t.name} className="flex items-start justify-between gap-4 pt-3 border-t border-[#e7e9ee] first:border-t-0 first:pt-0" data-testid={`dispatch-tech-${t.id || t.name}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-[15px] text-[#0e1420]">
                  {t.name}
                  {t.alt_availability && (
                    <span className="ml-2 text-[11px] font-normal text-[color:var(--cc-ink-muted)]">
                      ({t.alt_availability})
                    </span>
                  )}
                </div>
                {/* Close rate + Claire provenance — 2026-07-31 ranking-logic revision. */}
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  {hasCr ? (
                    <>
                      <span
                        className="font-mono-data text-[12.5px] font-semibold text-[#0e1420]"
                        data-testid={`dispatch-tech-close-rate-${t.id || t.name}`}
                      >
                        Close rate {Math.round(cr)}%
                      </span>
                      <span
                        className="text-[9.5px] font-mono-data uppercase tracking-widest border rounded px-1.5 py-0.5 bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]"
                        title={`AI-extracted from ServiceTitan's Big Board PDF by Claude. Field close rate (tech converting his own dispatched job)${closeRatesWeek ? ` — week of ${closeRatesWeek}` : ""}.`}
                        data-testid={`dispatch-tech-close-rate-provenance-${t.id || t.name}`}
                      >
                        Source: Claire / Big Board PDF
                      </span>
                    </>
                  ) : (
                    <span
                      className="text-[11.5px] italic text-[color:var(--cc-ink-muted)]"
                      data-testid={`dispatch-tech-close-rate-missing-${t.id || t.name}`}
                      title="No close-rate value on file for this tech in the latest Claire snapshot. Sorted to the bottom of the tier group."
                    >
                      No close rate on file
                    </span>
                  )}
                </div>
                <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1.5 leading-relaxed max-w-md">
                  {t.notes || "—"}
                </div>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-[color:var(--cc-ink-muted)]">
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={11} strokeWidth={1.8} />
                    {(t.home_territory || []).join(", ") || "no territory"}
                    {t.cross_territory && <span className="ml-1 text-[#0072bc] font-semibold">· cross-OK</span>}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <ShieldCheck size={11} strokeWidth={1.8} /> {LICENSE_LABEL[t.license] || t.license}
                  </span>
                </div>
              </div>
              <span
                className="shrink-0 font-mono-data text-[11px] px-2 py-1 rounded-md bg-[#f5f6f8] text-[#4b5563] border border-[#e7e9ee]"
                data-testid={`dispatch-tech-badge-${t.tier}`}
              >
                {TECH_BADGE_LABEL(t.tier)}
              </span>
            </div>
            );
          })
        )}
      </div>

      {/* Contextual notes */}
      {(usedCrossTerritory || permitGated || finalTier === "VeryHigh") && (
        <div className="space-y-2">
          {permitGated && (
            <div className="rounded-lg p-3 text-[12.5px] leading-relaxed border bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" data-testid="dispatch-note-license-gate">
              <b>License gate:</b> Permit-required or very-high-tier work — routing restricted to master/journeyman-licensed techs only.
            </div>
          )}
          {usedCrossTerritory && (
            <div className="rounded-lg p-3 text-[12.5px] leading-relaxed border bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" data-testid="dispatch-note-cross-territory">
              <b>Cross-territory fallback:</b> No one lives in this location for the applied filters — showing techs willing to cross territory instead.
            </div>
          )}
          {(finalTier === "VeryHigh" || permit) && (
            <div className="rounded-lg p-3 text-[12.5px] leading-relaxed border bg-[#fff0f0] text-[#7a1c1c] border-[#f5c2c2] flex items-start gap-2" data-testid="dispatch-note-escalation">
              <AlertTriangle size={14} strokeWidth={2} className="mt-0.5 shrink-0" />
              <div>
                {finalTier === "VeryHigh" && (
                  <p className="font-semibold">Very-high-tier escalation fired to Kathryn + Brett.</p>
                )}
                {permit && (
                  <p className="font-semibold">Permit-flagged escalation fired to Kathryn + Brett.</p>
                )}
                <p className="mt-1">
                  {alertFired
                    ? statsLoaded
                      ? "Alert logged to platform_alerts (mock-Slack). Book the job now — don't wait on the alert."
                      : "Alert queued."
                    : "Alert log unavailable this turn — proceed with dispatch anyway, admin will retry manually."}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-[color:var(--cc-ink-muted)] flex items-center gap-2 pt-1">
        <RefreshCw size={11} strokeWidth={1.8} />
        Data pulled live from Tech Profiles + Dispatch Job Stats. Change any input to re-evaluate.
      </div>
    </div>
  );
}
